import { EMOJI, REACTIONS, type ChatClient, type Reaction } from './client';
import type { Copy } from './i18n';

/**
 * The reaction bar and the floating emoji. A viewer's own clicks float at
 * once; everyone else's arrive with each chat read as a burst, scaled down
 * (one animation per few reactions) and capped, so a big audience fills the
 * screen with a few emoji a second, not thousands.
 */
const MAX_BURST = 12;
const MAX_FLOATING = 28;

export function mountReactions(bar: HTMLElement, layer: HTMLElement, client: ChatClient, t: Copy) {
  bar.classList.add('jc-react');
  bar.setAttribute('role', 'group');
  bar.setAttribute('aria-label', 'Reactions');
  bar.hidden = true;
  for (const r of REACTIONS) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'jc-react-btn';
    b.dataset.reaction = r;
    b.textContent = EMOJI[r];
    b.setAttribute('aria-label', t.react(t.reactions[r] ?? r));
    bar.append(b);
  }
  layer.classList.add('jc-floats');
  layer.setAttribute('aria-hidden', 'true');

  const reduced = matchMedia('(prefers-reduced-motion: reduce)');

  function float(r: Reaction, own: boolean) {
    if (reduced.matches || layer.childElementCount >= MAX_FLOATING) return;
    const el = document.createElement('span');
    el.className = own ? 'jc-float is-own' : 'jc-float';
    el.textContent = EMOJI[r];
    el.style.setProperty('--x', `${Math.round(Math.random() * 70)}%`);
    el.style.setProperty('--drift', `${Math.round((Math.random() - 0.5) * 60)}px`);
    el.style.setProperty('--dur', `${(2.2 + Math.random() * 1.2).toFixed(2)}s`);
    el.addEventListener('animationend', () => el.remove());
    layer.append(el);
  }

  bar.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-reaction]');
    if (!b) return;
    e.stopPropagation();
    const r = b.dataset.reaction as Reaction;
    client.react(r);
    float(r, true);
    b.classList.remove('is-pop');
    void b.offsetWidth;
    b.classList.add('is-pop');
  });

  const off = client.onRead((read) => {
    bar.hidden = !read.reactions_enabled;
    const entries = Object.entries(read.reactions).filter(([k, n]) => n > 0 && k in EMOJI) as [Reaction, number][];
    const total = entries.reduce((s, [, n]) => s + n, 0);
    if (!total) return;
    // Scale to at most MAX_BURST animations, spread over the poll interval.
    const scale = Math.max(1, total / MAX_BURST);
    for (const [r, n] of entries) {
      const shows = Math.max(1, Math.round(n / scale));
      for (let i = 0; i < shows; i++) window.setTimeout(() => float(r, false), Math.random() * 1800);
    }
  });

  return {
    destroy() {
      off();
      bar.replaceChildren();
      layer.replaceChildren();
    },
  };
}
